import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  HeartHandshake, Plus, Search, Filter, RefreshCw, LayoutGrid, List,
  Clock, CreditCard, Activity, CheckCircle2, UserCheck, AlertCircle,
  Phone, Calendar, ChevronRight, ArrowRight, User, ShieldCheck, DollarSign,
  Send, ExternalLink, XCircle, RotateCcw
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import opheService from '../../services/ophe.service';
import OpheLeadCreateModal from './OpheLeadCreateModal';

export default function OphePipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';

  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    stage1_payment: 0,
    stage2_active_care: 0,
    stage3_completed: 0,
    lost: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'kanban'
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [leadToRestore, setLeadToRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const handleRestoreLead = async (lead) => {
    try {
      setRestoring(true);
      await api.patch(`/leads/${lead._id}/status`, { status: 'new' });
      toast.success(`Patient "${lead.patientName}" restored to active OPHE pipeline!`);
      setLeadToRestore(null);
      await fetchPipelineData();
    } catch (err) {
      console.error('Failed to restore OPHE lead:', err);
      toast.error(err.response?.data?.message || 'Failed to restore lead');
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    fetchPipelineData();
  }, [activeTab, selectedCategory]);

  const loadCategories = async () => {
    try {
      const res = await opheService.getCategories({ status: 'active' });
      setCategories(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  const fetchPipelineData = async () => {
    try {
      setLoading(true);
      const [sumRes, leadsRes] = await Promise.all([
        opheService.getPipelineSummary(),
        opheService.getPipelineLeads({
          tab: activeTab,
          categoryId: selectedCategory || undefined,
          limit: 100
        })
      ]);

      if (sumRes.data?.success) {
        setSummary(sumRes.data.data);
      }
      if (leadsRes.data?.success) {
        setLeads(leadsRes.data.data);
      }
    } catch (error) {
      console.error('Error loading OPHE pipeline:', error);
      toast.error('Failed to load OPHE pipeline data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const filteredLeads = leads.filter(l => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const name = l.patientName?.toLowerCase() || '';
    const phone = l.patientPhone?.toLowerCase() || '';
    const pkg = l.opheServiceDetails?.packageName?.toLowerCase() || '';
    const prov = l.opheProviderData?.providerName?.toLowerCase() || '';
    return name.includes(q) || phone.includes(q) || pkg.includes(q) || prov.includes(q);
  });

  // Stage Definition (Stage 1 = Payment)
  const kanbanStages = [
    {
      step: 1,
      id: 'payment',
      title: 'Stage 1: Payment',
      subtitle: 'Razorpay Link & Collection',
      color: 'border-amber-500 text-amber-700 bg-amber-50/50',
      badgeBg: 'bg-amber-100 text-amber-800',
      filter: (l) => l.displayStage === 1 && l.status !== 'not_interested'
    },
    {
      step: 2,
      id: 'active_care',
      title: 'Stage 2: Active Care',
      subtitle: 'Provider Assigned & Active',
      color: 'border-teal-500 text-teal-700 bg-teal-50/50',
      badgeBg: 'bg-teal-100 text-teal-800',
      filter: (l) => l.displayStage === 2 && l.status !== 'not_interested'
    },
    {
      step: 3,
      id: 'completed',
      title: 'Stage 3: Completed',
      subtitle: 'Care Settled & Payout Done',
      color: 'border-emerald-500 text-emerald-700 bg-emerald-50/50',
      badgeBg: 'bg-emerald-100 text-emerald-800',
      filter: (l) => l.displayStage === 3 || l.status === 'completed'
    }
  ];

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-[1600px] mx-auto min-h-screen">
      
      {/* 🚀 Header & Action Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
              {activeTab === 'lost' ? 'OPHE Lost Leads' : 'OPHE Care Pipeline'}
            </h1>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${
              activeTab === 'lost'
                ? 'bg-rose-50 text-rose-700 border-rose-200'
                : 'bg-teal-50 text-teal-800 border-teal-200'
            }`}>
              {activeTab === 'lost' ? 'Lost & Cancelled Cases' : 'Stage 1: Payment Model'}
            </span>
          </div>
          <p className="text-xs text-slate-500 font-medium mt-1">
            {activeTab === 'lost'
              ? 'Review, track reasons, and restore lost or cancelled OPHE care cases'
              : 'Stage-by-Stage Elderly & Home Care Operations, Payments, Caregivers & Payouts'}
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={() => fetchPipelineData()}
            className="p-2.5 rounded-xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200 shadow-2xs transition-all flex items-center justify-center cursor-pointer"
            title="Refresh Pipeline"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-teal-600' : ''}`} />
          </button>


          <button
            type="button"
            onClick={() => navigate('/leads/create')}
            className="px-4 py-2.5 rounded-xl bg-[#143E78] hover:bg-indigo-900 text-white font-bold text-xs shadow-md shadow-indigo-900/15 transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>New OPHE Request</span>
          </button>
        </div>
      </div>

      {/* 🔍 Search & Filter Toolbar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        
        {/* Left: Search input */}
        <div className="relative w-full md:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search patient, phone, package, provider..."
            className="w-full text-xs pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/50 focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none transition-all"
          />
        </div>

        {/* Middle & Right: Stage Filter, Category Filter & View Toggle */}
        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end flex-wrap">
          
          {/* Pipeline Stage / Cases Dropdown */}
          <select
            value={activeTab}
            onChange={(e) => handleTabChange(e.target.value)}
            className="text-xs font-bold px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100/80 text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer shadow-2xs"
          >
            <option value="all">All Active Cases ({summary.total || 0})</option>
            <option value="payment">Stage 1: Payment ({summary.stage1_payment || 0})</option>
            <option value="active">Stage 2: Active Care ({summary.stage2_active_care || 0})</option>
            <option value="completed">Stage 3: Completed ({summary.stage3_completed || 0})</option>
            <option value="lost">Lost Cases ({summary.lost || 0})</option>
          </select>

          {/* Category dropdown */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="text-xs font-medium px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All Care Categories</option>
            {categories.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                viewMode === 'table'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Table</span>
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                viewMode === 'kanban'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Kanban</span>
            </button>
          </div>
        </div>

      </div>

      {/* 🚀 Main Content: Kanban or Table View */}
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
          <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
          <p className="text-sm font-bold text-slate-600">Loading OPHE Pipeline Cases...</p>
        </div>
      ) : activeTab === 'lost' ? (
        /* 💔 DEDICATED OPHE LOST LEADS VIEW */
        filteredLeads.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200/80 p-12 text-center max-w-lg mx-auto shadow-sm my-8">
            <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4 border border-rose-100">
              <XCircle className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No OPHE Lost Leads</h3>
            <p className="text-xs text-slate-500 mt-1 mb-6">
              {searchTerm
                ? 'No lost OPHE cases match your search query.'
                : 'There are currently no lost or cancelled leads in the OPHE care pipeline.'}
            </p>
            <button
              onClick={() => handleTabChange('all')}
              className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs shadow-lg shadow-teal-600/20 transition-all inline-flex items-center gap-2"
            >
              <span>Back to Active OPHE Pipeline</span>
            </button>
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
                <h2 className="text-sm font-bold text-slate-800">
                  OPHE Lost & Cancelled Leads ({filteredLeads.length})
                </h2>
              </div>
              <p className="text-xs text-slate-400">
                Only OPHE care inquiries and bookings marked as not interested
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredLeads.map((lead) => (
                <div
                  key={lead._id}
                  className="bg-white rounded-2xl border border-slate-200/90 hover:border-rose-300 hover:shadow-lg transition-all duration-200 p-5 flex flex-col justify-between relative overflow-hidden group"
                >
                  {/* Top Accent bar in rose */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-rose-500 to-amber-500" />

                  <div>
                    {/* Patient Name & Status */}
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-bold text-base text-slate-900 group-hover:text-rose-700 transition-colors">
                          {lead.patientName || 'Unnamed Patient'}
                        </h4>
                        <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          <span>{lead.patientPhone || 'No phone'}</span>
                          {lead.patientAge && <span>• {lead.patientAge}y</span>}
                          {lead.patientGender && <span className="capitalize">• {lead.patientGender}</span>}
                        </p>
                      </div>
                      <span className="px-2.5 py-1 text-[10px] font-black uppercase rounded-lg bg-rose-50 text-rose-700 border border-rose-200 tracking-wider">
                        Lost Lead
                      </span>
                    </div>

                    {/* Package & Pricing Box */}
                    <div className="mt-4 bg-slate-50/80 rounded-xl p-3 border border-slate-100 space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-800 truncate">
                          {lead.opheServiceDetails?.packageName || 'OPHE Care Package'}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-teal-50 text-teal-700 border border-teal-100">
                          {lead.opheServiceDetails?.duration || 'Standard'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200/50">
                        <span className="text-slate-500 text-[11px]">Quoted Amount</span>
                        <span className="font-black text-slate-900">
                          ₹{lead.opheServiceDetails?.quotedPrice ? Number(lead.opheServiceDetails.quotedPrice).toLocaleString('en-IN') : '0'}
                        </span>
                      </div>
                    </div>

                    {/* Lost Reason Box */}
                    <div className="mt-3 bg-rose-50/50 rounded-xl p-3 border border-rose-100/70 text-xs">
                      <div className="flex items-center gap-1.5 text-rose-800 font-bold text-[11px] mb-1">
                        <AlertCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                        <span>Lost Reason / Note</span>
                      </div>
                      <p className="text-slate-600 text-[11px] leading-relaxed">
                        {lead.notInterestedReason || lead.leadStatusReason || 'Lead closed or customer marked as not interested.'}
                      </p>
                    </div>

                    {/* Provider Info (if assigned) */}
                    {lead.opheProviderData?.providerName && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-slate-600 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                        <UserCheck className="w-3.5 h-3.5 text-slate-500" />
                        <span className="font-semibold text-slate-800 truncate">
                          {lead.opheProviderData.providerName}
                        </span>
                        <span className="text-[10px] text-slate-400">({lead.opheProviderData.providerRole || 'Provider'})</span>
                      </div>
                    )}
                  </div>

                  {/* Footer with Actions */}
                  <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-slate-400">
                      Lost {new Date(lead.updatedAt || lead.createdAt).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setLeadToRestore(lead)}
                        className="px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-[#143E78] font-bold text-xs transition-colors flex items-center gap-1.5"
                        title="Restore lead back to active OPHE pipeline"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Restore</span>
                      </button>
                      <button
                        onClick={() => navigate(`/ophe/leads/${lead._id}`)}
                        className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors flex items-center gap-1"
                      >
                        <span>View</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
                <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  OPHE Lost Leads Table ({filteredLeads.length})
                </h2>
              </div>
              <span className="text-xs text-slate-500">Only OPHE Care Lost Records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                    <th className="py-3 px-4">Patient Profile</th>
                    <th className="py-3 px-4">Care Package</th>
                    <th className="py-3 px-4">Quoted Pricing</th>
                    <th className="py-3 px-4">Lost Reason</th>
                    <th className="py-3 px-4">Date Lost</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredLeads.map((lead) => (
                    <tr key={lead._id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900">{lead.patientName || 'Unnamed'}</div>
                        <div className="text-slate-500 text-[11px]">{lead.patientPhone}</div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="font-semibold text-slate-800">
                          {lead.opheServiceDetails?.packageName || 'OPHE Care'}
                        </span>
                        <div className="text-[11px] text-teal-600">
                          {lead.opheServiceDetails?.duration || 'Standard'}
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-black text-slate-900">
                          ₹{lead.opheServiceDetails?.quotedPrice ? Number(lead.opheServiceDetails.quotedPrice).toLocaleString('en-IN') : '0'}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 max-w-xs">
                        <span className="text-slate-600 text-[11px] line-clamp-2">
                          {lead.notInterestedReason || lead.leadStatusReason || 'Not Interested'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-slate-500 text-[11px]">
                        {new Date(lead.updatedAt || lead.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3.5 px-4 text-right space-x-2">
                        <button
                          onClick={() => setLeadToRestore(lead)}
                          className="px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-[#143E78] font-bold text-xs transition-colors inline-flex items-center gap-1"
                        >
                          <RotateCcw className="w-3 h-3" />
                          <span>Restore</span>
                        </button>
                        <button
                          onClick={() => navigate(`/ophe/leads/${lead._id}`)}
                          className="px-3 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-700 font-bold text-xs transition-colors"
                        >
                          Open Case
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : filteredLeads.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-12 text-center max-w-lg mx-auto shadow-sm my-8">
          <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mx-auto mb-4 border border-teal-100">
            <HeartHandshake className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">No OPHE Cases Found</h3>
          <p className="text-xs text-slate-500 mt-1 mb-6">
            {searchTerm
              ? 'No cases match your search query.'
              : 'There are currently no cases in this pipeline view. Create a new OPHE care intake.'}
          </p>
          <button
            onClick={() => navigate('/leads/create')}
            className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs shadow-lg shadow-teal-600/20 transition-all inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create First OPHE Case</span>
          </button>
        </div>
      ) : viewMode === 'kanban' ? (
        /* KANBAN BOARD VIEW (3 STAGES) */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {kanbanStages.map((stage) => {
            const stageLeads = filteredLeads.filter(stage.filter);
            return (
              <div
                key={stage.id}
                className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3 min-h-[520px]"
              >
                {/* Column Header */}
                <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                  <div>
                    <h3 className="font-bold text-sm text-slate-800 tracking-tight">{stage.title}</h3>
                    <p className="text-[11px] text-slate-400 font-medium">{stage.subtitle}</p>
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-black ${stage.badgeBg}`}>
                    {stageLeads.length}
                  </span>
                </div>

                {/* Cards Container */}
                <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-320px)] pr-1">
                  {stageLeads.length === 0 ? (
                    <div className="py-12 text-center text-slate-400 text-xs italic">
                      No cases in this stage
                    </div>
                  ) : (
                    stageLeads.map((lead) => (
                      <div
                        key={lead._id}
                        onClick={() => navigate(`/ophe/leads/${lead._id}`)}
                        className="bg-white rounded-xl border border-slate-200 hover:border-teal-500 hover:shadow-lg transition-all duration-200 p-4 cursor-pointer group relative overflow-hidden"
                      >
                        {/* Top Accent bar */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                        {/* Patient Name & Age */}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-bold text-sm text-slate-900 group-hover:text-teal-700 transition-colors">
                              {lead.patientName}
                            </h4>
                            <p className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                              <span>{lead.patientPhone}</span>
                              {lead.patientAge && <span>• {lead.patientAge}y</span>}
                              {lead.patientGender && <span className="capitalize">• {lead.patientGender}</span>}
                            </p>
                          </div>
                        </div>

                        {/* Package Details */}
                        <div className="mt-3 bg-teal-50/50 rounded-lg p-2.5 border border-teal-100/60">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-teal-900 truncate">
                              {lead.opheServiceDetails?.packageName || 'OPHE Care Package'}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-teal-100 text-teal-800">
                              {lead.opheServiceDetails?.duration || 'Day'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-1.5 text-xs">
                            <span className="font-black text-slate-900">
                              ₹{lead.opheServiceDetails?.quotedPrice ? Number(lead.opheServiceDetails.quotedPrice).toLocaleString('en-IN') : '0'}
                            </span>
                            <span className={`text-[10px] font-bold ${
                              lead.ophePaymentData?.status === 'paid'
                                ? 'text-emerald-700'
                                : 'text-amber-700'
                            }`}>
                              {lead.ophePaymentData?.status === 'paid' ? '● Payment Done' : '○ Payment Pending'}
                            </span>
                          </div>
                        </div>

                        {/* Care Provider Assigned (if any) */}
                        {lead.opheProviderData?.providerName && (
                          <div className="mt-2.5 flex items-center gap-2 text-[11px] text-slate-600 bg-slate-50 p-2 rounded-lg border border-slate-100">
                            <UserCheck className="w-3.5 h-3.5 text-teal-600" />
                            <span className="font-semibold text-slate-800 truncate">
                              {lead.opheProviderData.providerName}
                            </span>
                            <span className="text-[10px] text-slate-400">({lead.opheProviderData.providerRole || 'Attendant'})</span>
                          </div>
                        )}

                        {/* Footer card link */}
                        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                          <span>Updated {new Date(lead.updatedAt || lead.createdAt).toLocaleDateString()}</span>
                          <span className="text-teal-600 font-bold group-hover:translate-x-0.5 transition-transform flex items-center gap-0.5">
                            Manage Stage {lead.displayStage || 1} <ChevronRight className="w-3 h-3" />
                          </span>
                        </div>

                      </div>
                    ))
                  )}
                </div>

              </div>
            );
          })}
        </div>
      ) : (
        /* DATA TABLE VIEW */
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                  <th className="py-3 px-4">Patient Profile</th>
                  <th className="py-3 px-4">Care Package</th>
                  <th className="py-3 px-4">Duration & Pricing</th>
                  <th className="py-3 px-4">Payment Status</th>
                  <th className="py-3 px-4">Assigned Provider</th>
                  <th className="py-3 px-4">Pipeline Stage</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredLeads.map((lead) => (
                  <tr key={lead._id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-slate-900">{lead.patientName}</div>
                      <div className="text-slate-500 text-[11px]">{lead.patientPhone}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="font-semibold text-slate-800">
                        {lead.opheServiceDetails?.packageName || 'OPHE Care'}
                      </span>
                      <div className="text-[11px] text-teal-600">
                        {lead.opheServiceDetails?.categoryId?.name || 'General Care'}
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="font-bold text-slate-900">
                        ₹{lead.opheServiceDetails?.quotedPrice ? Number(lead.opheServiceDetails.quotedPrice).toLocaleString('en-IN') : '0'}
                      </div>
                      <div className="text-[11px] text-slate-500">{lead.opheServiceDetails?.duration || 'Day'} package</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        lead.ophePaymentData?.status === 'paid'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {lead.ophePaymentData?.status === 'paid' ? 'Paid' : 'Payment Due'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      {lead.opheProviderData?.providerName ? (
                        <div>
                          <span className="font-semibold text-slate-900">{lead.opheProviderData.providerName}</span>
                          <div className="text-[10px] text-slate-500">{lead.opheProviderData.providerRole}</div>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">Not Assigned</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${
                        lead.displayStage === 1
                          ? 'bg-amber-50 text-amber-800 border-amber-200'
                          : lead.displayStage === 2
                          ? 'bg-teal-50 text-teal-800 border-teal-200'
                          : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      }`}>
                        {lead.displayStage === 1
                          ? 'Stage 1: Payment'
                          : lead.displayStage === 2
                          ? 'Stage 2: Active Care'
                          : 'Stage 3: Completed'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <button
                        onClick={() => navigate(`/ophe/leads/${lead._id}`)}
                        className="px-3 py-1.5 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-700 font-bold text-xs transition-colors"
                      >
                        Open Case
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Restore Lead Confirmation Modal */}
      {leadToRestore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
          <div className="bg-white rounded-3xl border border-slate-100 shadow-2xl max-w-md w-full overflow-hidden p-6 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-[#143E78] flex items-center justify-center border border-indigo-100">
              <RotateCcw className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Restore OPHE Lead?</h3>
              <p className="text-xs text-slate-500 mt-1">
                Are you sure you want to restore <strong>{leadToRestore.patientName}</strong> ({leadToRestore.patientPhone}) back to the active OPHE Care pipeline?
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setLeadToRestore(null)}
                disabled={restoring}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleRestoreLead(leadToRestore)}
                disabled={restoring}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-[#143E78] hover:bg-indigo-900 text-white transition-all shadow-md shadow-indigo-900/20 flex items-center gap-2 cursor-pointer"
              >
                {restoring ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                <span>Yes, Restore to Pipeline</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create OPHE Request Modal */}
      <OpheLeadCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => fetchPipelineData()}
      />

    </div>
  );
}
